/**
 * Map + prd2-aligned driving / camera (see prd2.md).
 */
/**
 * 1 world unit ≈ 1 m (car + physics). Smaller HALF/SIZE than early builds so each
 * grid cell is ~100 m across — huge ~280 m “super blocks” made 180 km/h feel slow.
 */
export const MAP = {
  HALF: 1000,
  SIZE: 2000,
  GRID: 10,
  ROAD_WIDTH: 14,
  SIDEWALK_WIDTH: 3,
} as const;

/**
 * Poly Haven asphalt_02 scan is ~3 m per texture tile; UV repeat uses world meters / this.
 */
export const ROAD_ASPHALT_METERS_PER_TILE = 3;

/** Legacy FOV base; chase cam overrides with CAMERA_CHASE.BASE_FOV. */
export const CAMERA = {
  INITIAL_DISTANCE: 2800,
  MIN_ORBIT: 400,
  MAX_ORBIT: 9000,
  FOV: 52,
  /** Low enough to inspect road surface when chase zoom is pulled in. */
  NEAR: 0.06,
  FAR: 3000,
} as const;

/** Low, wide proportions — larger on screen vs roads / traffic (GLB models scale to LENGTH). */
export const CAR = {
  WIDTH: 3.0,
  HEIGHT: 1.38,
  LENGTH: 6.2,
  COLLISION_RADIUS: 2.55,
} as const;

/** prd2 Week 2 Day 8–9 — extraction goal, cop spawn, bust radius. */
export const GAME = {
  EXTRACTION_X: -520,
  EXTRACTION_Z: 520,
  EXTRACTION_RADIUS: 26,
  EXTRACTION_HOLD_S: 1.5,
  /** Contact bust — very small; main bust is the meter (slower fill). */
  BUST_RADIUS: 2.8,
  COP_SPAWN_Z: MAP.HALF * 0.38,
  COP_SPAWN_X: 0,
  /** Second AI unit — single-player / quick play only (offset so they don’t stack). */
  COP2_SPAWN_X: -260,
  COP2_SPAWN_Z: MAP.HALF * 0.33,
} as const;

/**
 * prd2 physics — tuned for our arcade integrator (m/s, grip as lateral damping).
 *
 * Day 1 (prd2 Week 1): weight + momentum via yaw inertia; drift bonus; car pitch in GameView.
 */
export const PHYSICS = {
  /** Base longitudinal push (m/s²); curve + top-speed falloff applied in vehicle.ts. */
  ACCEL: 32,
  /**
   * Yaw inertia: yaw rate eases toward steering demand (rad/s² catch-up, 1/s time constant).
   * Higher = snappier; handbrake path uses higher for aggressive drift entry.
   */
  YAW_RESPONSE: 7.2,
  YAW_RESPONSE_HANDBRAKE: 12.5,
  /** Powerslide: tiny forward shove — prd2 “DRIFT_SPEED_BONUS” arcade equivalent (m/s²). */
  DRIFT_FWD_BONUS_ACCEL: 1.15,
  /** Reverse gear pulls weaker than forward (typical FWD / transmission feel). */
  REVERSE_ACCEL_MULT: 0.52,
  MAX_SPEED_KMH: 260,
  MAX_SPEED_MS: 260 / 3.6,
  /**
   * Nitro — strict rules (see vehicle.ts):
   * - Tank starts empty; refill only while cruising (speed + throttle gates), not in cooldown.
   * - Burst only with forward momentum, committed throttle, no handbrake, enough reserve.
   * - Cooldown after each burst so you can’t chain-tap.
   */
  /** prd2 Day 11: nitro as burst weapon — top speed during burst ≈ base × multiplier (capped). */
  NITRO_SPEED_MULT: 1.7,
  NITRO_MAX_SPEED_KMH: 320,
  NITRO_MAX_SPEED_MS: 320 / 3.6,
  NITRO_EXTRA_ACCEL: 45,
  /** prd2: burst length + long cooldown (last resort). */
  NITRO_DURATION_S: 2.2,
  NITRO_SPOOL_S: 0.1,
  NITRO_MIN_TANK_TO_USE: 0.92,
  /** Need this much ground speed (m/s) before nitro can engage (~40 km/h). */
  NITRO_MIN_SPEED_MS: 11,
  /** Must be rolling forward; reverse disables nitro. */
  NITRO_MIN_FORWARD_MS: 1.5,
  NITRO_MIN_THROTTLE: 0.5,
  NITRO_COOLDOWN_S: 11,
  NITRO_RECHARGE_MIN_SPEED_MS: 12.5,
  NITRO_RECHARGE_MIN_THROTTLE: 0.4,
  NITRO_RECHARGE_PER_S: 0.028,
  MAX_REVERSE_KMH: 60,
  MAX_REVERSE_MS: 60 / 3.6,
  BRAKE_FORCE: 0.92,
  STEER_SPEED_LOW: 2.45,
  STEER_SPEED_HIGH: 0.92,
  STEER_SPEED_THRESHOLD_KMH: 80,
  /** Below this ground speed (m/s), steering adds no yaw — no “spin in place”. */
  STEER_MIN_SPEED_MS: 0.7,
  /** Ramp steering authority from 0→1 between STEER_MIN_SPEED_MS and this (m/s). */
  STEER_RAMP_TO_FULL_MS: 3.4,
  HANDBRAKE_STEER: 1.68,
  /** Extra yaw at speed when handbrake = powerslide. */
  HANDBRAKE_STEER_FAST: 2.25,
  /** Forward speed bleed per frame scale while drifting (higher = more scrub). */
  HANDBRAKE_FWD_DRAG: 0.991,
  /** High = velocity snaps to heading quickly (Burnout-style grip). */
  GRIP_NORMAL: 0.82,
  /** Low = rear slides (drift) — used for slow handbrake turns only. */
  GRIP_DRIFT: 0.14,
  /** Coast — slightly less decay = sustained speed feels more exciting. */
  DRAG_COAST: 0.994,
  DRAG_BRAKE: 0.91,
  WALL_RESTITUTION: 0.35,
} as const;

/** Burnout-style police takedowns — local arcade rules, intentionally lightweight. */
export const TAKEDOWN = {
  CONTACT_RADIUS: CAR.COLLISION_RADIUS * 2.2,
  MIN_ATTACK_SPEED_MS: 17,
  MIN_CLOSING_MS: 7.5,
  SIDE_DOT_MIN: 0.46,
  PIT_SPEED_MS: 13,
  PIT_REAR_DOT_MIN: 0.42,
  BRAKE_CHECK_DIST: 8.5,
  BRAKE_CHECK_CLOSING_MS: 12,
  COOLDOWN_FRAMES: 42,
  COP_STUN_S: 1.35,
  IMPULSE_BASE: 18,
  IMPULSE_SIDE: 22,
  SCORE_BASE: 420,
} as const;

/** Heat 3+ police hazards — small pool, obvious warnings, no heavy physics bodies. */
export const POLICE_HAZARDS = {
  MAX_ACTIVE: 4,
  SPAWN_CHECK_S: 2.4,
  ROADBLOCK_COOLDOWN_S: 8,
  SPIKE_COOLDOWN_S: 10,
  MIN_SPAWN_DIST: 92,
  MAX_SPAWN_DIST: 168,
  DESPAWN_DIST: 260,
  WARNING_DIST: 115,
  WARNING_DOT_MIN: 0.62,
  ROADBLOCK_LENGTH: MAP.ROAD_WIDTH * 0.92,
  ROADBLOCK_DEPTH: 2.6,
  SPIKE_LENGTH: MAP.ROAD_WIDTH * 0.82,
  SPIKE_DEPTH: 1.25,
  HIT_COOLDOWN_FRAMES: 55,
  ROADBLOCK_SPEED_LOSS: 0.34,
  ROADBLOCK_NITRO_PIERCE_SPEED_KEEP: 0.72,
  SPIKE_SPEED_LOSS: 0.62,
  SPIKE_NITRO_DRAIN: 0.42,
} as const;

/** Replayability layer — rotating one-run contracts that change how extraction is earned. */
export const RUN_CONTRACTS = {
  TAKEDOWN_TARGET: 3,
  CHAOS_BILL_TARGET: 2200,
  PURSUIT_BREAKER_TARGET: 1,
  STYLE_POINTS_TARGET: 2200,
  FAST_ESCAPE_TARGET_S: 58,
  EXTRACTION_BLOCK_COOLDOWN_FRAMES: 95,
} as const;

/** prd2 Week 1 Day 5 — airborne / ramps. */
export const AIRBORNE = {
  GRAVITY: 9.8,
  /** Limited yaw in air (deg/s at full steer). */
  STEER_DEG_PER_S: 52,
  /** Heading vs launch beyond this = bad landing. */
  BAD_LANDING_ANGLE_DEG: 32,
  BAD_LANDING_SPEED_RETAIN: 0.52,
  BAD_LANDING_YAW_KICK: 2.8,
  /** Skip building collision when center of mass this high (m). */
  CLEAR_BUILDINGS_Y: 2.8,
} as const;

/**
 * prd2 § Camera — Day 2: lead, speed zoom, nitro FOV/tilt, shake (collision + nitro).
 */
export const CAMERA_CHASE = {
  HEIGHT: 30,
  /** Extra pitch toward horizon — more cinematic (prd2 suggested ~22°; we keep readable). */
  TILT_DEGREES: 13,
  /** How much tilt lifts camera Y (small = less overhead). */
  TILT_HEIGHT_SCALE: 0.058,
  /** Base chase distance (m); tighter = more parallax / speed read. */
  DISTANCE_BEHIND: 30,
  LEAD_FACTOR: 8.8,
  /** Scales velocity → look-ahead offset on target cam. */
  LEAD_SCALE: 0.46,
  /** Mixes lead into look-at point (0–1). */
  LEAD_LOOK_MIX: 0.48,
  LOOK_HEIGHT: 3.2,
  /** Wider FOV = more motion at screen edges at the same km/h. */
  BASE_FOV: 62,
  /** Extra FOV at max speed when nitro is actively boosting. */
  FOV_NITRO_EXTRA: 11,
  /** Base extra FOV from speed alone (before nitro punch). */
  FOV_SPEED_EXTRA: 23,
  /** Extra camera pitch (deg) while nitro boosting — “punched forward” feel. */
  NITRO_TILT_EXTRA_DEG: 5.5,
  /**
   * <1 pulls chase cam farther at high speed (prd2 ZOOM_AT_MAX_SPEED ~0.72).
   * Slightly softer here so 120 km/h still feels fast without going aerial.
   */
  ZOOM_AT_MAX_SPEED: 0.82,
  /** Blend how much speed affects auto zoom (0 = off). */
  SPEED_ZOOM_BLEND: 0.42,
  POSITION_SMOOTHING: 5.5,
  ROTATION_SMOOTHING: 4.2,
  /** Screen shake: scales collision impulse into 0–1 range. */
  SHAKE_COLLISION_SCALE: 0.018,
  /** Shake strength when nitro engages (additive 0–1). */
  SHAKE_NITRO_ON: 0.32,
  /** Per-frame decay at 60fps (prd2 ~0.88). */
  SHAKE_DECAY: 0.88,
  /** Max world-units offset at shake = 1. */
  SHAKE_MAX_OFFSET: 2.4,
  /** User zoom multiplier on chase distance — min = camera nearly on the pavement for texture inspection. */
  ZOOM_MIN: 0.02,
  ZOOM_MAX: 14,
  ZOOM_SMOOTH: 12,
} as const;

/** prd2 Week 1 Day 3 — trails, drift smoke, nitro flames, tire marks, sparks. Optimized for performance. */
export const VFX = {
  TRAIL_SEGMENTS: 10,
  TRAIL_WIDTH_MAX: 0.4,
  TRAIL_Y: 0.07,
  TRAIL_ALPHA: 0.55,
  SMOKE_COUNT: 12,
  SMOKE_LIFE_S: 1.0,
  SMOKE_SPAWN_PER_S: 16,
  SMOKE_SIZE: 0.55,
  NITRO_COUNT: 18,
  NITRO_LIFE_S: 0.3,
  NITRO_SPAWN_PER_S: 50,
  NITRO_SIZE: 0.42,
  SPARK_COUNT: 8,
  SPARK_LIFE_S: 0.15,
  TIRE_MAX_POINTS: 1500,
  TIRE_WIDTH: 0.55,
  TIRE_Y: 0.045,
  TIRE_MIN_DIST: 0.7,
  TIRE_OPACITY: 0.78,
} as const;

/** Minimap / HUD — sync extraction with GAME; ramps approximate elevated deck / kickers. */
export const WORLD_MARKERS = {
  EXTRACTION: { x: GAME.EXTRACTION_X, z: GAME.EXTRACTION_Z },
  /** Approximate jump / elevated deck areas (prd2 ramp ring). */
  RAMPS: [
    { x: 740, z: 0 },
    { x: -740, z: 0 },
    { x: 0, z: 760 },
    { x: 0, z: -760 },
    { x: 520, z: 420 },
  ] as const,
} as const;

/** prd2 road taxonomy — metadata for tooling / future generator passes. */
export const ROAD_TYPE = {
  HIGHWAY: 'highway',
  MAIN: 'main',
  ALLEY: 'alley',
  CURVED: 'curved',
  RAMP: 'ramp',
  UNDERPASS: 'underpass',
} as const;
export type RoadType = (typeof ROAD_TYPE)[keyof typeof ROAD_TYPE];

/**
 * Ground XZ strips under elevated highway decks (see World.addElevatedHighway).
 * Used for muffled engine + darker mood (audio + HUD).
 */
export const TUNNEL_UNDERPASS_ZONES = [
  { minX: -900, maxX: 900, minZ: 720, maxZ: 805 },
  { minX: 700, maxX: 780, minZ: -900, maxZ: 900 },
] as const;

export function isInTunnelUnderpass(x: number, z: number): boolean {
  return TUNNEL_UNDERPASS_ZONES.some(
    (b) => x >= b.minX && x <= b.maxX && z >= b.minZ && z <= b.maxZ,
  );
}

/**
 * Frame budget + quality toggles. Post stack: `?perf=0` disables radial blur.
 */
export const PERFORMANCE = {
  TARGET_FPS: 60,
  MOBILE_PIXEL_RATIO_MAX: 1.0,
  DESKTOP_PIXEL_RATIO_MAX: 1.25,
  POST_PROCESSING_ENABLED:
    typeof window === 'undefined' ||
    new URLSearchParams(window.location.search).get('perf') !== '0',
} as const;

/** prd2 Day 7 — session timer (seconds) for HUD countdown. */
export const SESSION_LENGTH_S = 90;

/** prd2 Week 3 Day 15–16 — multiplayer server tick + port. */
export const NET = {
  PORT: 8765,
  /** Fixed timestep for authoritative simulation (ms). */
  TICK_MS: 50,
} as const;

/** prd2 Week 2 Day 13 — dynamic music layer mixing (Web Audio, no assets). */
export const MUSIC = {
  LAST_30_S: 30,
  LAST_10_S: 10,
  /** Master gain for procedural bed when MP3 BGM is not playing (ducked when BGM runs). */
  MASTER: 0.052,
  /** Local file under `public/` — see `public/audio/`. */
  BGM_URL: '/audio/eminem-till-i-collapse-hd_RVeOyfRA.mp3',
  /** Base playback rate before cinematic slow-mo multiplier. */
  BGM_BASE_PLAYBACK_RATE: 1.1,
} as const;

/** Night city atmosphere — 2am neon noir. */
export const NIGHT = {
  /** Sky color — pure black with slight blue tint. */
  SKY_COLOR: 0x020408,
  /** Fog color — dark blue-purple haze. */
  FOG_COLOR: 0x0a0812,
  /** Ambient light — very dim blue. */
  AMBIENT_COLOR: 0x1a2040,
  AMBIENT_INTENSITY: 0.15,
  /** Moon light — cold blue directional. */
  MOON_COLOR: 0x8090c0,
  MOON_INTENSITY: 0.25,
  /** Stars count - optimized. */
  STAR_COUNT: 200,
} as const;

/** Rain particle system - optimized for performance. */
export const RAIN = {
  PARTICLE_COUNT: 400,
  /** Rain fall speed (m/s). */
  FALL_SPEED: 35,
  /** Horizontal spread around camera. */
  SPREAD_XZ: 120,
  /** Vertical spawn height above camera. */
  SPAWN_HEIGHT: 60,
  /** Rain angle from vertical (radians) — wind effect. */
  ANGLE: 0.15,
  /** Particle length based on speed. */
  STREAK_LENGTH: 1.8,
  /** Rain opacity. */
  OPACITY: 0.35,
} as const;

/** Wet road reflections. */
export const WET_ROAD = {
  /** Base reflectivity of wet surface. */
  REFLECTIVITY: 0.65,
  /** How much neon lights reflect. */
  NEON_REFLECT_STRENGTH: 0.8,
  /** Roughness of wet surface. */
  ROUGHNESS: 0.15,
} as const;

/** Street lamps - optimized spacing. */
export const STREET_LAMP = {
  /** Spacing between lamps along roads - increased for performance. */
  SPACING: 80,
  /** Light color — warm orange sodium. */
  COLOR: 0xffa040,
  /** Light intensity. */
  INTENSITY: 1.2,
  /** Light distance. */
  DISTANCE: 45,
  /** Pole height. */
  HEIGHT: 8,
} as const;

/** Neon signs on buildings - optimized. */
export const NEON_SIGNS = {
  COUNT: 26,
  TEXTS: [
    'LAST MILE',
    'SKYWAY',
    'NOIR',
    '24H',
    'CASINO',
    'NOODLES',
    'CLUB',
    'DINER',
    'MOTEL',
    'LIVE',
    'DRONE NET',
    'ECHO',
    'ROOFTOP',
    'PIZZA',
    'CAFE',
  ],
  COLORS: [0x00ffff, 0xff00ff, 0xffff00, 0xff0066, 0x00ff66, 0xff6600],
} as const;

/** Premium skyline layer — decorative only, tuned for desktop/laptop competition demos. */
export const PREMIUM_CITY = {
  SKYLINE_RING_COUNT: 3,
  SEARCHLIGHT_COUNT: 5,
  BEACON_COUNT: 18,
  DRONE_TRAFFIC_COUNT: 14,
  SEARCHLIGHT_HEIGHT: 185,
  SEARCHLIGHT_RADIUS: 960,
  DRONE_ALTITUDE_MIN: 110,
  DRONE_ALTITUDE_MAX: 240,
} as const;

/** Lightweight holographic route guidance — procedural meshes only. */
export const PREMIUM_ROUTE = {
  CHEVRON_COUNT: 22,
  CHEVRON_START_DIST: 18,
  CHEVRON_END_PAD: 24,
  CHEVRON_WIDTH: 5.2,
  CHEVRON_LENGTH: 7.4,
  CHEVRON_Y: 0.115,
  BEAM_HEIGHT: 72,
  BEAM_RADIUS: 8.5,
  RING_COUNT: 4,
} as const;

/** Parked cars - optimized. */
export const PARKED_CARS = {
  COUNT: 20,
  COLORS: [0x1a1a2a, 0x2a1a1a, 0x1a2a1a, 0x2a2a1a, 0x1a1a1a, 0x3a2a2a, 0x2a3a3a],
} as const;

/** Cinematic camera director. */
export const CAMERA_DIRECTOR = {
  /** Chase cam — default following mode. */
  CHASE: {
    DISTANCE_MIN: 8,
    DISTANCE_MAX: 18,
    HEIGHT_MIN: 3,
    HEIGHT_MAX: 6,
    FOV_IDLE: 65,
    FOV_MAX_SPEED: 85,
    LAG_FACTOR: 0.12,
    DUTCH_ANGLE_MAX: 8,
  },
  /** Drift cam — side angle during handbrake. */
  DRIFT: {
    SIDE_OFFSET: 6,
    FOLLOW_SPEED_MULT: 0.6,
    TRANSITION_IN: 0.3,
    TRANSITION_OUT: 0.5,
  },
  /** Nitro cam — low ground-level burst. */
  NITRO: {
    HEIGHT: 1.2,
    DISTANCE: 6,
    FOV: 95,
    CHROMATIC_ABERRATION: 0.015,
    MOTION_BLUR_MULT: 1.8,
  },
  /** Ramp cam — bullet time on jumps. */
  RAMP: {
    TIME_SCALE: 0.3,
    DURATION: 1.5,
    PULLBACK_DISTANCE: 25,
    ORBIT_SPEED: 0.8,
  },
  /** Cop near cam — tension cut. */
  COP_NEAR: {
    TRIGGER_DISTANCE: 5,
    HOLD_DURATION: 0.4,
    SIDE_ANGLE: Math.PI / 4,
  },
  /** Collision cam — impact freeze. */
  COLLISION: {
    FREEZE_DURATION: 0.2,
    PERPENDICULAR_SNAP: true,
    SHAKE_SCALE: 2.5,
  },
  /** Extraction cam — wide shot near goal. */
  EXTRACTION: {
    TRIGGER_DISTANCE: 50,
    PULLBACK_MULT: 1.8,
    TINT_INTENSITY: 0.3,
  },
} as const;

/** Enhanced physics feel. */
export const PHYSICS_FEEL = {
  /** Slip angle model. */
  SLIP_ANGLE_PEAK: 0.15,
  SLIP_ANGLE_FALLOFF: 0.08,
  /** Wall scrub instead of bounce. */
  WALL_SCRUB_FRICTION: 0.82,
  WALL_SCRUB_SPEED_LOSS: 0.08,
  WALL_SPARK_THRESHOLD_KMH: 60,
  /** Weight transfer visual. */
  PITCH_BRAKE_MAX: 0.12,
  PITCH_ACCEL_MAX: 0.08,
  ROLL_MAX: 0.1,
  /** Drift progression. */
  DRIFT_ENTRY_THRESHOLD: 0.34,
  DRIFT_CATCH_COUNTERSTEER: 0.6,
  DRIFT_SNAP_RELEASE: 0.8,
  /** Surface types grip multipliers. */
  SURFACE_ROAD: 1.0,
  SURFACE_WET: 0.8,
  SURFACE_GRASS: 0.6,
  /** Landing wobble. */
  LANDING_WOBBLE_DURATION: 0.3,
  LANDING_WOBBLE_AMPLITUDE: 0.05,
} as const;
